import React, { useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import { DocItemModal } from "features/DocItemModal/DocItemModal";
import { DocumentsData } from "constant-data/documentsData";

import { SearchInput } from "components/SearchInput/SearchInput";
import { Button } from "components/Button/Button";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";
import { Table } from "components/Table/Table";
import { RowRender } from "components/RowRender/RowRender";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { MediumText } from "components/MediumText/MediumText";
import { Paragraph } from "components/Paragraph/Paragraph";

import { modal } from "utils/nodes";

import {
  modal2Atom,
  modalAtom,
  openItemGroupsSelectModalAtom,
  setConfirmationModalAtom,
  setInfoModalAtom,
  setModal2CloseAtom,
  setModalCloseAtom,
} from "../../atoms/modalAtom/modalAtom";
import {
  TDocItem,
  TDocItemToDraw,
  TDocumentMainColumn,
} from "../../types/documents";
import {
  docItemsAtom,
  docItemsToDrawAtom,
  openedDocItemAtom,
  openedDocItemStateAtom,
  openedDocumentAtom,
  selectedDocItemAtom,
  selectedDocumentTypeAtom,
} from "../../atoms/documentsAtom";
import useDocuments from "../../hooks/useDocuments";

import styles from "./styles.module.scss";

type BodyProps = {
  columns?: TDocumentMainColumn[];
  bottomRow?: ({
    filteredDocItems,
  }: {
    filteredDocItems: TDocItemToDraw[];
  }) => TDocItemToDraw;
};

export const Body = ({ columns, bottomRow }: BodyProps): JSX.Element => {
  const { removeDocItem } = useDocuments();
  const [, setModal2] = useAtom(modal2Atom);
  const [openedDocument] = useAtom(openedDocumentAtom);
  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const [docItems] = useAtom(docItemsAtom);
  const [docItemsToDraw] = useAtom(docItemsToDrawAtom);
  const [selectedDocItem, setSelectedDocItem] = useAtom(selectedDocItemAtom);
  const [, setOpenedDocItem] = useAtom(openedDocItemAtom);
  const [, setOpenedDocItemState] = useAtom(openedDocItemStateAtom);
  const [, setConfirmModal] = useAtom(setConfirmationModalAtom);
  const [, setInfoModal] = useAtom(setInfoModalAtom);
  const [, openItemGroupsSelectModal] = useAtom(openItemGroupsSelectModalAtom);

  const [searchVal, setSearchVal] = useState("");
  const [filteredDocItems, setFilteredDocItems] = useState<TDocItemToDraw[]>(
    []
  );

  const onAddClick = () => {
    if (!selectedDocumentType?.id) return;

    const { shouldCheck2Stores } =
      DocumentsData[selectedDocumentType?.id] || {};

    if (
      openedDocument?.storeid &&
      (!shouldCheck2Stores || openedDocument?.store2id)
    ) {
      setModal2({
        isOpen: true,
        children: <DocItemModal />,
      });
    } else {
      setInfoModal({
        title: !shouldCheck2Stores
          ? "Вы не можете добавлять товары, пока не выбран и не сохранён склад"
          : "Вы не можете добавлять товары, пока не выбраны и не сохранены оба склада",
        atomToOpen: modal2Atom,
        setAtomClose: setModal2CloseAtom,
        height: 240,
      });
    }
  };

  const onEditClick = () => {
    if (!selectedDocItem) return;
    setOpenedDocItem({ ...selectedDocItem });
    setModal2({
      isOpen: true,
      children: <DocItemModal />,
    });
  };

  const onDeleteClick = () => {
    if (!selectedDocItem) return;
    setConfirmModal({
      title: `Вы действительно хотите удалить этот товар ("${selectedDocItem.item?.name}")?`,
      onConfirm: removeDocItem,
      atomToOpen: modal2Atom,
      setAtomClose: setModal2CloseAtom,
    });
  };

  const onCopyClick = () => {
    if (!selectedDocItem) return;
    setOpenedDocItemState({
      docitemid: selectedDocItem.docitemid,
      itemid: selectedDocItem.item?.itemid,
      item2id: selectedDocItem.item2?.itemid,
      measureid: selectedDocItem.measure?.measureid,
      measure2id: selectedDocItem.measure2?.measureid,
      pricelistid: selectedDocItem.pricelist?.pricelistid,
      price: selectedDocItem.price,
      amount: selectedDocItem.amount,
      realamount: selectedDocItem.realamount,
      total: selectedDocItem.total,
      salepercent: selectedDocItem.salepercent,
      frontprice: selectedDocItem.frontprice,
    } as any);
    setModal2({
      isOpen: true,
      children: <DocItemModal />,
    });
  };

  const colDefs = useMemo(
    () =>
      columns?.map((i) => ({
        ...i,
        cellRenderer: RowRender,
        resizable: true,
        suppressMovable: false,
      })),
    [columns]
  );

  const onRowClicked = (event) => {
    // Если нажали на нижнюю закрепленную строку
    if (event.node.rowPinned) return;

    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map((node) => node.data);

    if (selectedData[0]?.docitemid) {
      if (selectedData[0].docitemid === selectedDocItem?.docitemid) {
        onEditClick();
      } else {
        setSelectedDocItem(
          docItems.find((i) => i.docitemid === selectedData[0].docitemid)
        );
      }
    }
  };

  const onFillLeftAmountClick = () => {
    if (!selectedDocumentType?.id) return;

    if (!openedDocument?.storeid) {
      return setInfoModal({
        title:
          "Вы не можете заполнять товары по остатку, пока не выбран и не сохранён склад",
        atomToOpen: modal2Atom,
        setAtomClose: setModal2CloseAtom,
        height: 240,
      });
    }

    openItemGroupsSelectModal();
  };

  useEffect(() => {
    if (!searchVal) return setFilteredDocItems(docItemsToDraw);

    setFilteredDocItems(
      docItemsToDraw?.filter((i) =>
        i.item?.toLowerCase()?.includes(searchVal.toLowerCase())
      ) || []
    );
  }, [searchVal, docItemsToDraw]);

  return (
    <div className={styles.bodyContainer}>
      <div className="flex items-center">
        <div style={{ width: 300 }}>
          <SearchInput callback={setSearchVal} />
        </div>
        <Button icon="plus" onClick={onAddClick} />
        <HorizontalSpace size={11} />
        <Button icon="edit" onClick={onEditClick} disabled={!selectedDocItem} />
        <HorizontalSpace size={11} />
        <Button
          icon="delete"
          onClick={onDeleteClick}
          disabled={!selectedDocItem}
        />
        <HorizontalSpace size={11} />
        <Button
          className="mr-2"
          icon="copy"
          disabled={!selectedDocItem}
          onClick={onCopyClick}
        />
        {selectedDocumentType?.id === "8" && (
          <>
            <HorizontalSpace size={11} />
            <Button
              className="mr-2"
              // icon="copy"
              onClick={onFillLeftAmountClick}
              children={"Заполнить по остатку"}
            />
          </>
        )}
        {/*<Button className="mr-2" icon="copy">
          Загрузить из Excel
        </Button>*/}
        {/*<NamedSelect
          className="mr-2"
          options={[]}
          placeholder="Просмотреть цены"
        />
        <NamedSelect
          className="mr-2"
          options={[]}
          placeholder="Терминал сбора данных"
        />*/}
      </div>
      <VerticalSpace size={20} />
      <Table
        height={300}
        rowData={filteredDocItems}
        colDefs={colDefs as any}
        rowSelection={"single"}
        onRowClicked={onRowClicked}
        rowStyle={{ cursor: "pointer" }}
        getRowStyle={(params) => ({
          backgroundColor:
            params.data.docitemid === selectedDocItem?.docitemid
              ? "var(--secondaryColor)"
              : "unset",
          fontWeight:
            params.data.docitemid === selectedDocItem?.docitemid
              ? "500"
              : "unset",
          color:
            params.data.docitemid === selectedDocItem?.docitemid
              ? "var(--primaryColor)"
              : "unset",
        })}
        pinnedBottomRowData={
          bottomRow && filteredDocItems?.length
            ? [bottomRow({ filteredDocItems })]
            : undefined
        }
      />
    </div>
  );
};
